import {
  ESdChangeReason,
  ICommonSettings,
  ILanguageModel,
  ILocationPermissionLabelSetting
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigLocationSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingLocationSetting?: ILocationPermissionLabelSetting;
  location?: ILocationPermissionLabelSetting["values"];
}

export interface IConfigLocationSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingLocationId?: string;
  languages: ILanguageModel[];
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigLocationSettings extends React.PureComponent<
  IConfigLocationSettingsProps,
  IConfigLocationSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigLocationSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingLocationSetting: undefined,
      location: []
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigLocationSettingsProps) {
    if (prevProps.editingLocationId && !this.props.editingLocationId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingLocationId !== this.props.editingLocationId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingLocationId) {
      const setting = this.props.commonSettings.locationPermissionLabelSettings?.find(
        (x) => x.id === this.props.editingLocationId
      );
      this.setState({ editingLocationSetting: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();
          fieldsValue.name = setting.name;
          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({ location: setting.values });
        }
      });
    } else {
      const values: any[] = [];
      this.props.languages.forEach((language) => {
        values.push({
          languageName: language.shortName,
          cultureName: language.name,
          value: ""
        });
      });
      this.setState({ location: values });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.locationPermissionLabelSettings;

    const isSettingAlreadyExist =
      settings?.some((x) => x.name === value && x.id !== this.props.editingLocationId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("GeoLocation Setting name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: ILocationPermissionLabelSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      values: this.state.location?.map((location) => ({
        ...location,
        value: location.value?.trim()
      }))
    };
    if (this.props.editingLocationId) {
      obj.id = this.props.editingLocationId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "locationPermissionLabelSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "locationPermissionLabelSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingLocationId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const parentDataKey = this.props.formInputName[0] === "androidConfig" ? "iosConfig" : "androidConfig";

    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingLocationId || "",
      configKey: "locationPermissionLabelSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`GeoLocation setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [[parentDataKey, this.props.formInputName[1]]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
          This GeoLocation setting is
          used on ${
            this.props.formInputName[0] === "androidConfig" ? "IOS" : "Android"
          } section. Please discard this setting
          on other config section before delete.
        `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  setValue = (e, lang) => {
    const location = _.cloneDeep(this.state.location);
    location?.forEach((value) => {
      if (value.cultureName === lang.cultureName) {
        value.value = e.target.value;
      }
    });
    this.setState({ location });
  };

  getSectionDescription = (): React.ReactNode => {
    const contentKey =
      this.props.formInputName[0] === "androidConfig"
        ? "locationpermissionlabelssettingspopup_locationpermissionlabelssettings_description_android"
        : "locationpermissionlabelssettingspopup_locationpermissionlabelssettings_description_ios";

    return this.props.getSectionDescription(contentKey);
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="GeoLocation setting"
        secondHeader="GeoLocation setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingLocationId ? this.deleteSetting : undefined}
            deleteTestId="geolocationModalDeleteButton"
            submitTestId={`geolocationModal${this.props.editingLocationId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingLocationId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configLocationForm"
          />
        }
      >
        <Form
          id="configLocationForm"
          name="locationSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody description={this.getSectionDescription()}>
            <ConfigField
              label="Name"
              required
              tooltipContentKey="locationpermissionlabelssettingspopup_locationpermissionlabelssettings_name"
            >
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a GeoLocation Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "GeoLocation Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="iosGeolocationSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            {this.state.location?.map((value) => {
              return (
                <ConfigField
                  key={value.cultureName}
                  label={value.languageName}
                  tooltipContentKey="locationpermissionlabelssettingspopup_locationpermissionlabelssettings_english"
                >
                  <div style={{ width: 380, marginBottom: 24 }}>
                    <KuikaInput
                      size="md"
                      data-testid={`geolocationSettingPermissionTextInput-${value.languageName}`}
                      value={value.value}
                      onChange={(e) => this.setValue(e, value)}
                    />
                  </div>
                </ConfigField>
              );
            })}
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}
